import { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { db } from "../lib/firebase";
import { collection, getDocs, doc, updateDoc, setDoc, addDoc, deleteDoc, query, where, writeBatch, serverTimestamp } from "firebase/firestore";
import { getBookingStatusInfo, MAX_BOOKING_DAYS } from "../lib/constants";
import { useSettings } from "../lib/SettingsContext";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Users, CalendarCheck, CreditCard, TrendingUp, Eye, EyeOff, Pencil, Check, Download, Package, Trash2, Plus } from "lucide-react";
import * as XLSX from "xlsx";
import { AdminAddBookingDialog, AdminAddUserDialog } from "../components/AdminDialogs";
import { Spinner, PageLoader } from "../components/Spinner";
import { Calendar as CalendarIcon, X as XIcon } from "lucide-react";
import { Calendar as DayPicker } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function Admin() {
  const settings = useSettings();
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [subs, setSubs] = useState([]);
  const [tab, setTab] = useState("bookings");
  const [filter, setFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [processingSubId, setProcessingSubId] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null); // { id, newStatus, currentStatus }
  const [mergingDup, setMergingDup] = useState(false);
  const [showRevenue, setShowRevenue] = useState(false);
  const [confirmMerge, setConfirmMerge] = useState(false);
  const [cars, setCars] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [priceDraft, setPriceDraft] = useState("");
  const [priceNoteDraft, setPriceNoteDraft] = useState("");
  const [savingPriceId, setSavingPriceId] = useState(null);
  const [historyFor, setHistoryFor] = useState(null); // { id, name, phone }
  const [products, setProducts] = useState([]);
  const [newProduct, setNewProduct] = useState({ name: "", price: "", quantity: "" });
  const [savingProduct, setSavingProduct] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [productDraft, setProductDraft] = useState({ price: "", quantity: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [bSnap, uSnap, sSnap, cSnap, pSnap] = await Promise.all([
        getDocs(collection(db, "bookings")),
        getDocs(collection(db, "users")),
        getDocs(collection(db, "subscriptions")),
        getDocs(collection(db, "cars")),
        getDocs(collection(db, "products")),
      ]);
      setBookings(bSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.date + (b.timeSlot || "")).localeCompare(a.date + (a.timeSlot || ""))));
      setUsers(uSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((u) => !u.merged));
      setSubs(sSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setCars(cSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setProducts(pSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateBookingStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      const booking = bookings.find((b) => b.id === id);
      await updateDoc(doc(db, "bookings", id), { status });

      // Deduct from subscription ONLY if the booking explicitly used a package
      // (booking.useSubscription === true) AND it's the first time hitting "completed".
      if (status === "completed" && booking && booking.status !== "completed" && booking.useSubscription && booking.subscriptionId) {
        const subRef = doc(db, "subscriptions", booking.subscriptionId);
        const subQ = query(
          collection(db, "subscriptions"),
          where("uid", "==", booking.uid),
          where("status", "==", "active")
        );
        const subSnap = await getDocs(subQ);
        const subDoc = subSnap.docs.find((d) => d.id === booking.subscriptionId) || subSnap.docs[0];
        if (subDoc) {
          const data = subDoc.data();
          const remaining = Math.max(0, (data.washesRemaining || 0) - 1);
          const updates = { washesRemaining: remaining };
          if (remaining === 0) updates.status = "exhausted";
          await updateDoc(subRef, updates);
          toast.success(`Completed · 1 wash deducted (${remaining} left in ${data.planName})`);
        } else {
          toast.success("Status updated (no active package to deduct from)");
        }
      } else {
        toast.success("Status updated");
      }
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUpdatingId(null);
    }
  };

  const startEditPrice = (booking) => {
    setEditingPriceId(booking.id);
    setPriceDraft(String(booking.price ?? ""));
    setPriceNoteDraft(booking.priceNote || "");
  };

  const cancelEditPrice = () => {
    setEditingPriceId(null);
    setPriceDraft("");
    setPriceNoteDraft("");
  };

  const saveCustomPrice = async (booking) => {
    const amount = Number(priceDraft);
    if (!Number.isFinite(amount) || amount < 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setSavingPriceId(booking.id);
    try {
      const note = priceNoteDraft.trim();
      const updates = { price: amount, priceNote: note };
      // Keep the original suggested price on record the first time it's overridden.
      if (booking.suggestedPrice === undefined && amount !== booking.price) {
        updates.suggestedPrice = booking.price;
      }
      await updateDoc(doc(db, "bookings", booking.id), updates);
      toast.success("Price updated");
      setEditingPriceId(null);
      setPriceDraft("");
      setPriceNoteDraft("");
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingPriceId(null);
    }
  };

  const updateSetting = async (key, value) => {
    try {
      await setDoc(doc(db, "settings", "app"), { [key]: value }, { merge: true });
      toast.success("Setting updated");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const addProduct = async () => {
    const name = newProduct.name.trim();
    const price = Number(newProduct.price);
    const quantity = Number(newProduct.quantity);
    if (!name) { toast.error("Product name is required"); return; }
    if (!Number.isFinite(price) || price < 0) { toast.error("Enter a valid price"); return; }
    if (!Number.isFinite(quantity) || quantity < 0) { toast.error("Enter a valid quantity"); return; }
    setSavingProduct(true);
    try {
      await addDoc(collection(db, "products"), { name, price, quantity, createdAt: serverTimestamp() });
      toast.success(`Added ${name}`);
      setNewProduct({ name: "", price: "", quantity: "" });
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingProduct(false);
    }
  };

  const startEditProduct = (p) => {
    setEditingProductId(p.id);
    setProductDraft({ price: String(p.price ?? ""), quantity: String(p.quantity ?? "") });
  };

  const cancelEditProduct = () => {
    setEditingProductId(null);
    setProductDraft({ price: "", quantity: "" });
  };

  const saveProductEdit = async (p) => {
    const price = Number(productDraft.price);
    const quantity = Number(productDraft.quantity);
    if (!Number.isFinite(price) || price < 0 || !Number.isFinite(quantity) || quantity < 0) {
      toast.error("Enter valid values");
      return;
    }
    try {
      await updateDoc(doc(db, "products", p.id), { price, quantity });
      toast.success("Product updated");
      setEditingProductId(null);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const removeProduct = async (p) => {
    try {
      await deleteDoc(doc(db, "products", p.id));
      toast.success(`Removed ${p.name}`);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  /** Finds users sharing the same phone number (usually a manually-added
   *  record plus the real account created when they later logged in via
   *  OTP), keeps the real one (the one WITHOUT the `manual` flag), re-points
   *  its bookings/subscriptions/cars, and marks the rest merged.
   *
   *  A customer's first login already attempts this same carry-over itself
   *  (copying each record to the real uid, then best-effort deleting the
   *  original). If that cleanup delete failed, the original is still sitting
   *  here tagged `uid == other.id` even though canonical already has a copy
   *  of it (marked with `copiedFromId` pointing back at it) — re-pointing it
   *  again would create a visible duplicate. So for each collection we first
   *  look at what canonical already owns; anything the customer already
   *  carried over gets its leftover original deleted instead of re-copied. */
  const mergeDuplicateCustomers = async () => {
    setMergingDup(true);
    try {
      const uSnap = await getDocs(collection(db, "users"));
      const all = uSnap.docs.map((d) => ({ id: d.id, ref: d.ref, ...d.data() })).filter((u) => !u.merged && u.phone);
      const byPhone = {};
      all.forEach((u) => { (byPhone[u.phone] = byPhone[u.phone] || []).push(u); });
      const dupGroups = Object.values(byPhone).filter((g) => g.length > 1);

      if (dupGroups.length === 0) {
        toast.success("No duplicate phone numbers found");
        return;
      }

      let mergedCount = 0;
      let dedupedCount = 0;
      for (const group of dupGroups) {
        const canonical = group.find((u) => !u.manual) || group[0];
        const others = group.filter((u) => u.id !== canonical.id);
        const batch = writeBatch(db);
        for (const colName of ["bookings", "subscriptions", "cars"]) {
          const canonicalSnap = await getDocs(query(collection(db, colName), where("uid", "==", canonical.id)));
          const alreadyCopiedIds = new Set(
            canonicalSnap.docs.map((d) => d.data().copiedFromId).filter(Boolean)
          );
          for (const other of others) {
            const ownedSnap = await getDocs(query(collection(db, colName), where("uid", "==", other.id)));
            ownedSnap.forEach((d) => {
              if (alreadyCopiedIds.has(d.id)) {
                batch.delete(d.ref);
                dedupedCount++;
              } else {
                batch.update(d.ref, { uid: canonical.id });
              }
            });
          }
        }
        for (const other of others) {
          batch.update(other.ref, { merged: true, mergedInto: canonical.id });
        }
        await batch.commit();
        mergedCount += others.length;
      }
      toast.success(
        `Merged ${mergedCount} duplicate record(s) across ${dupGroups.length} phone number(s)`
        + (dedupedCount > 0 ? ` · removed ${dedupedCount} already-copied leftover(s)` : "")
      );
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setMergingDup(false);
    }
  };

  const filteredBookings = (() => {
    let list = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);
    if (dateFilter) {
      const dateStr = dateFilter.toISOString().split("T")[0];
      list = list.filter((b) => b.date === dateStr);
    }
    return list;
  })();
  const activeSubs = subs.filter((s) => s.status === "active");
  const todayBookings = bookings.filter((b) => b.date === new Date().toISOString().split("T")[0]);
  const totalRevenue = bookings.filter((b) => b.status === "completed").reduce((a, b) => a + (b.price || 0), 0);
  const activeAtOutlet = todayBookings
    .filter((b) => b.status === "pending" || b.status === "confirmed")
    .sort((a, b) => (a.timeSlot || "").localeCompare(b.timeSlot || ""));

  const carsByUid = useMemo(() => {
    const map = {};
    cars.forEach((c) => {
      if (!c.uid) return;
      if (!map[c.uid]) map[c.uid] = [];
      map[c.uid].push(c);
    });
    return map;
  }, [cars]);

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const userCars = carsByUid[u.id] || [];
      return (
        (u.name || "").toLowerCase().includes(q) ||
        (u.phone || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        userCars.some((c) => (c.registration || "").toLowerCase().includes(q))
      );
    });
  }, [users, carsByUid, customerSearch]);

  const historyBookings = useMemo(() => {
    if (!historyFor) return [];
    return bookings
      .filter((b) => b.uid === historyFor.id)
      .sort((a, b) => (b.date + (b.timeSlot || "")).localeCompare(a.date + (a.timeSlot || "")));
  }, [bookings, historyFor]);

  const historyStats = useMemo(() => {
    const completed = historyBookings.filter((b) => b.status === "completed");
    return {
      totalVisits: historyBookings.length,
      totalSpent: completed.reduce((sum, b) => sum + (b.price || 0), 0),
    };
  }, [historyBookings]);

  const exportCustomersToExcel = () => {
    const rows = users.map((u) => {
      const userSub = subs.find((s) => s.uid === u.id && (s.status === "active" || s.status === "pending_approval"));
      const userCars = carsByUid[u.id] || [];
      const userBookings = bookings.filter((b) => b.uid === u.id);
      const completedBookings = userBookings.filter((b) => b.status === "completed");
      return {
        "Name": u.name || "",
        "Phone": u.phone || "",
        "Email": u.email || "",
        "Role": u.role || "",
        "Vehicles": userCars.map((c) => c.registration || "no reg").join(", "),
        "Subscription Plan": userSub?.planName || "",
        "Subscription Status": userSub?.status || "",
        "Washes Remaining": userSub?.washesRemaining ?? "",
        "Total Bookings": userBookings.length,
        "Completed Bookings": completedBookings.length,
        "Total Spent (INR)": completedBookings.reduce((sum, b) => sum + (b.price || 0), 0),
        "Joined": u.createdAt?.toDate
          ? u.createdAt.toDate().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
          : "",
      };
    });
    const sheet = XLSX.utils.json_to_sheet(rows);
    sheet["!cols"] = [
      { wch: 20 }, { wch: 14 }, { wch: 24 }, { wch: 10 }, { wch: 24 },
      { wch: 18 }, { wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 16 }, { wch: 14 }, { wch: 14 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Customers");
    const dateStr = new Date().toISOString().split("T")[0];
    XLSX.writeFile(workbook, `baywash-customers-${dateStr}.xlsx`);
    toast.success(`Exported ${rows.length} customer${rows.length === 1 ? "" : "s"}`);
  };

  const STATS = [
    { icon: CalendarCheck, label: "TODAY'S BOOKINGS", value: todayBookings.length },
    { icon: TrendingUp, label: "TOTAL BOOKINGS", value: bookings.length },
    { icon: CreditCard, label: "ACTIVE SUBS", value: activeSubs.length },
    { icon: Users, label: "USERS", value: users.length },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="font-mono-accent text-xs text-[#002FA7] mb-3">[ ADMIN · CONTROL ROOM ]</div>
          <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">
              Operations.
            </h1>
            <div className="flex gap-3 flex-wrap">
              <AdminAddBookingDialog users={users} cars={cars} products={products} onCreated={load} />
              <AdminAddUserDialog onCreated={load} />
            </div>
          </div>

          {/* STATS */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#D4D4D8] border border-[#D4D4D8] mb-12">
            {STATS.map((s, i) => (
              <div key={i} className="bg-white p-6">
                <s.icon size={20} strokeWidth={1.5} className="text-[#002FA7] mb-3" />
                <div className="font-mono-accent text-[10px] text-[#525252] mb-2">{s.label}</div>
                <div className="font-display font-black text-3xl">{s.value}</div>
              </div>
            ))}
          </div>
          <div className="bg-[#0A0A0A] text-white p-8 mb-12 flex items-center justify-between">
            <div className="font-mono-accent text-xs text-[#FFC000]">TOTAL REVENUE (CASH ON SERVICE)</div>
            <div className="flex items-baseline gap-4">
              <div className="font-display font-black text-4xl">
                {showRevenue ? `₹${totalRevenue.toLocaleString("en-IN")}` : "₹••••••"}
              </div>
              <button
                type="button"
                data-testid="admin-toggle-revenue-visibility"
                onClick={() => setShowRevenue((v) => !v)}
                aria-label={showRevenue ? "Hide total revenue" : "Show total revenue"}
                className="text-white/60 hover:text-white transition-colors"
              >
                {showRevenue ? <EyeOff size={20} strokeWidth={1.5} /> : <Eye size={20} strokeWidth={1.5} />}
              </button>
            </div>
          </div>

          {/* TABS */}
          <div className="flex gap-px bg-[#D4D4D8] mb-8 border-b border-[#D4D4D8]">
            {["bookings", "subscription_requests", "subscription_history", "customers", "products", "settings"].map((t) => {
              const labelMap = {
                bookings: "BOOKINGS",
                subscription_requests: `REQUESTS (${subs.filter((s) => s.status === "pending_approval").length})`,
                subscription_history: "SUBSCRIPTIONS",
                customers: `CUSTOMERS (${users.length})`,
                products: `PRODUCTS (${products.length})`,
                settings: "SETTINGS",
              };
              return (
                <button
                  key={t}
                  data-testid={`admin-tab-${t}`}
                  onClick={() => setTab(t)}
                  className={`bg-white px-6 py-4 font-mono-accent text-xs uppercase whitespace-nowrap ${tab === t ? "text-[#002FA7] border-b-2 border-[#002FA7]" : "text-[#525252]"}`}
                >
                  {labelMap[t]}
                </button>
              );
            })}
          </div>

          {tab === "bookings" && (
            <div>
              <div className="flex justify-between items-center mb-6 gap-3 flex-wrap">
                <h2 className="font-display font-bold text-2xl">All Bookings</h2>
                <div className="flex gap-2 flex-wrap items-center">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        data-testid="admin-date-filter-button"
                        type="button"
                        className="h-10 px-4 border border-[#D4D4D8] rounded-none flex items-center gap-2 text-sm font-mono hover:bg-[#F4F4F5]"
                      >
                        <CalendarIcon size={14} />
                        {dateFilter ? dateFilter.toISOString().split("T")[0] : "Any date"}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="rounded-none p-0 w-auto border-[#D4D4D8]">
                      <DayPicker
                        mode="single"
                        selected={dateFilter}
                        onSelect={setDateFilter}
                        className="rounded-none"
                      />
                    </PopoverContent>
                  </Popover>
                  {dateFilter && (
                    <button
                      data-testid="admin-clear-date-filter"
                      type="button"
                      onClick={() => setDateFilter(undefined)}
                      className="h-10 px-3 border border-[#D4D4D8] rounded-none flex items-center gap-1 text-xs font-mono-accent text-[#FF2A2A] hover:bg-[#FF2A2A] hover:text-white hover:border-[#FF2A2A]"
                    >
                      <XIcon size={12} /> CLEAR
                    </button>
                  )}
                  <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger data-testid="admin-filter-select" className="w-40 rounded-none border-[#D4D4D8]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="border border-[#D4D4D8] overflow-x-auto">
                <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-[#F4F4F5] font-mono-accent text-[10px] text-[#525252] min-w-[800px]">
                  <div className="col-span-2">CUSTOMER</div>
                  <div className="col-span-2">DATE / TIME</div>
                  <div className="col-span-2">SERVICE</div>
                  <div className="col-span-1">VEHICLE</div>
                  <div className="col-span-1">PRICE</div>
                  <div className="col-span-2">STATUS</div>
                  <div className="col-span-2">ACTION</div>
                </div>
                {loading && <PageLoader label="Loading bookings…" />}
                {!loading && filteredBookings.length === 0 && (
                  <div className="p-8 text-center text-sm text-[#525252]">No bookings.</div>
                )}
                {filteredBookings.map((b) => (
                  editingPriceId === b.id ? (
                    <div key={b.id} className="border-t border-[#D4D4D8] px-6 py-4 bg-[#F4F4F5] min-w-[800px]">
                      <div className="flex flex-wrap items-end gap-3">
                        <div className="mr-2">
                          <div className="font-bold text-sm">{b.userName}</div>
                          <div className="text-[#525252] text-xs font-mono">{b.vehicleLabel} · {b.serviceLabel}</div>
                        </div>
                        <div className="w-28">
                          <label className="font-mono-accent text-[10px] text-[#525252] block mb-1">CHARGED (₹)</label>
                          <Input
                            data-testid={`admin-price-input-${b.id}`}
                            type="number"
                            value={priceDraft}
                            onChange={(e) => setPriceDraft(e.target.value)}
                            className="h-9 rounded-none border-[#D4D4D8] text-sm"
                            autoFocus
                          />
                        </div>
                        <div className="flex-1 min-w-[220px]">
                          <label className="font-mono-accent text-[10px] text-[#525252] block mb-1">COMMENT (OPTIONAL)</label>
                          <Input
                            data-testid={`admin-price-note-${b.id}`}
                            value={priceNoteDraft}
                            onChange={(e) => setPriceNoteDraft(e.target.value)}
                            placeholder="e.g. loyalty discount, referral offer…"
                            className="h-9 rounded-none border-[#D4D4D8] text-sm"
                          />
                        </div>
                        <Button
                          data-testid={`admin-price-save-${b.id}`}
                          onClick={() => saveCustomPrice(b)}
                          disabled={savingPriceId === b.id}
                          className="h-9 bg-[#002FA7] hover:bg-[#001B6B] rounded-none text-xs px-4"
                        >
                          {savingPriceId === b.id ? <Spinner /> : "SAVE"}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          data-testid={`admin-price-cancel-${b.id}`}
                          onClick={cancelEditPrice}
                          disabled={savingPriceId === b.id}
                          className="h-9 rounded-none text-xs px-4 border-[#D4D4D8]"
                        >
                          CANCEL
                        </Button>
                      </div>
                    </div>
                  ) : (
                  <div key={b.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-t border-[#D4D4D8] items-center text-sm min-w-[800px]">
                    <div className="col-span-2">
                      <button
                        type="button"
                        data-testid={`customer-history-booking-${b.id}`}
                        onClick={() => setHistoryFor({ id: b.uid, name: b.userName, phone: b.userPhone })}
                        className="font-bold hover:text-[#002FA7] hover:underline text-left"
                      >
                        {b.userName}
                      </button>
                      <div className="text-[#525252] text-xs font-mono">{b.userPhone}</div>
                    </div>
                    <div className="col-span-2 font-mono text-xs">
                      <div>{b.date}</div>
                      <div className="text-[#525252]">{b.timeSlot}</div>
                    </div>
                    <div className="col-span-2 text-xs">
                      <div>{b.serviceLabel}</div>
                      {b.customServices?.length > 0 && (
                        <div className="text-[#525252] mt-0.5">
                          {b.customServices.map((s, i) => (
                            <div key={i} className="truncate">+ {s.name} (₹{s.fee})</div>
                          ))}
                        </div>
                      )}
                      {b.products?.length > 0 && (
                        <div className="text-[#525252] mt-0.5">
                          {b.products.map((p, i) => (
                            <div key={i} className="truncate">· {p.qty}× {p.name} (₹{p.price * p.qty})</div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="col-span-1 text-xs">{b.vehicleLabel}</div>
                    <div className="col-span-1">
                      <button
                        type="button"
                        data-testid={`admin-price-edit-${b.id}`}
                        onClick={() => startEditPrice(b)}
                        className="group flex items-center gap-1 font-bold hover:text-[#002FA7]"
                        title="Enter the actual amount charged"
                      >
                        ₹{b.price}
                        <Pencil size={11} className="text-[#D4D4D8] group-hover:text-[#002FA7]" />
                      </button>
                      {b.suggestedPrice !== undefined && b.suggestedPrice !== b.price && (
                        <div className="text-[9px] font-mono text-[#525252] line-through">₹{b.suggestedPrice}</div>
                      )}
                      {b.priceNote && (
                        <div className="text-[9px] text-[#525252] italic max-w-[100px] truncate" title={b.priceNote}>{b.priceNote}</div>
                      )}
                    </div>
                    <div className="col-span-2">
                      <span className={`text-[10px] font-mono-accent px-2 py-1 ${getBookingStatusInfo(b.status).color}`}>
                        {getBookingStatusInfo(b.status).label}
                      </span>
                    </div>
                    <div className="col-span-2 flex items-center gap-2">
                      <Select
                        value={b.status}
                        onValueChange={(v) => v !== b.status && setPendingStatus({ id: b.id, newStatus: v, currentStatus: b.status })}
                        disabled={updatingId === b.id}
                      >
                        <SelectTrigger data-testid={`admin-status-${b.id}`} className="w-32 h-8 rounded-none border-[#D4D4D8] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-none">
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                      {updatingId === b.id && <Spinner />}
                    </div>
                  </div>
                  )
                ))}
              </div>
            </div>
          )}

          {(tab === "subscription_requests" || tab === "subscription_history") && (
            <div>
              {tab === "subscription_requests" && (
                <h2 className="font-display font-bold text-2xl mb-6">Pending Approval Requests</h2>
              )}
              {tab === "subscription_history" && (
                <h2 className="font-display font-bold text-2xl mb-6">Subscription History</h2>
              )}
              <div className="border border-[#D4D4D8]">
                <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-[#F4F4F5] font-mono-accent text-[10px] text-[#525252]">
                  <div className="col-span-3">CUSTOMER</div>
                  <div className="col-span-2">PLAN</div>
                  <div className="col-span-1">PRICE</div>
                  <div className="col-span-2">CATEGORY</div>
                  <div className="col-span-2">RENEWS</div>
                  <div className="col-span-2">STATUS / ACTION</div>
                </div>
                {loading && <PageLoader label="Loading…" />}
                {!loading && (() => {
                  const list = tab === "subscription_requests"
                    ? subs.filter((s) => s.status === "pending_approval")
                    : subs.filter((s) => s.status !== "pending_approval");
                  if (list.length === 0) {
                    return <div className="p-8 text-center text-sm text-[#525252]">
                      {tab === "subscription_requests" ? "No pending requests." : "No subscriptions yet."}
                    </div>;
                  }
                  return list.map((s) => (
                    <div key={s.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-t border-[#D4D4D8] items-center text-sm">
                      <div className="col-span-3">
                        <div className="font-bold">{s.userName}</div>
                        <div className="text-[#525252] text-xs font-mono">{s.userPhone}</div>
                      </div>
                      <div className="col-span-2 font-bold">{s.planName}</div>
                      <div className="col-span-1 font-mono">₹{s.price}</div>
                      <div className="col-span-2 text-xs">{s.vehicleCategoryLabel || "—"}</div>
                      <div className="col-span-2 text-xs font-mono">{s.endDate}</div>
                      <div className="col-span-2">
                        {s.status === "pending_approval" ? (
                          <div className="flex gap-2 items-center">
                            <Button
                              data-testid={`approve-sub-${s.id}`}
                              disabled={processingSubId === s.id}
                              onClick={async () => {
                                setProcessingSubId(s.id);
                                try {
                                  await updateDoc(doc(db, "subscriptions", s.id), { status: "active", approvedAt: new Date().toISOString() });
                                  toast.success("Approved");
                                  load();
                                } finally { setProcessingSubId(null); }
                              }}
                              size="sm"
                              className="bg-[#00D05A] text-black hover:bg-[#0A0A0A] hover:text-white rounded-none font-mono-accent text-[10px] h-7 px-2"
                            >
                              {processingSubId === s.id ? <Spinner /> : "APPROVE"}
                            </Button>
                            <Button
                              data-testid={`reject-sub-${s.id}`}
                              disabled={processingSubId === s.id}
                              onClick={async () => {
                                setProcessingSubId(s.id);
                                try {
                                  await updateDoc(doc(db, "subscriptions", s.id), { status: "rejected" });
                                  toast.success("Rejected");
                                  load();
                                } finally { setProcessingSubId(null); }
                              }}
                              size="sm"
                              variant="ghost"
                              className="text-[#FF2A2A] hover:bg-[#FF2A2A] hover:text-white rounded-none font-mono-accent text-[10px] h-7 px-2"
                            >
                              {processingSubId === s.id ? <Spinner /> : "REJECT"}
                            </Button>
                          </div>
                        ) : (
                          <span className={`text-[10px] font-mono-accent px-2 py-1 ${
                            s.status === "active" ? "bg-[#00D05A] text-black"
                            : s.status === "rejected" ? "bg-[#FF2A2A] text-white"
                            : s.status === "exhausted" ? "bg-[#FFC000] text-black"
                            : "bg-[#D4D4D8] text-[#525252]"
                          }`}>
                            {s.status?.toUpperCase()}
                          </span>
                        )}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}

          {tab === "customers" && (
            <div>
              {!loading && (
                <div className="border border-[#002FA7] mb-8">
                  <div className="flex items-center gap-2 px-6 py-3 bg-[#002FA7] text-white">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                    </span>
                    <div className="font-mono-accent text-xs">ACTIVE AT OUTLET NOW ({activeAtOutlet.length})</div>
                  </div>
                  {activeAtOutlet.length === 0 ? (
                    <div className="p-6 text-center bg-[#F4F4F5]">
                      <div className="font-mono-accent text-xs text-[#525252]">No washes in progress right now</div>
                      <div className="text-xs text-[#525252] mt-1">Today's pending/confirmed bookings will show up here.</div>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-[#F4F4F5] font-mono-accent text-[10px] text-[#525252]">
                        <div className="col-span-3">CUSTOMER</div>
                        <div className="col-span-2">PHONE</div>
                        <div className="col-span-2">VEHICLE</div>
                        <div className="col-span-2">SERVICE</div>
                        <div className="col-span-1">TIME</div>
                        <div className="col-span-2">STATUS</div>
                      </div>
                      {activeAtOutlet.map((b) => (
                        <div key={b.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-t border-[#D4D4D8] items-center text-sm">
                          <div className="col-span-3 font-bold">
                            <button
                              type="button"
                              data-testid={`customer-history-outlet-${b.id}`}
                              onClick={() => setHistoryFor({ id: b.uid, name: b.userName, phone: b.userPhone })}
                              className="hover:text-[#002FA7] hover:underline text-left"
                            >
                              {b.userName || "—"}
                            </button>
                          </div>
                          <div className="col-span-2 font-mono text-xs text-[#525252]">{b.userPhone || "—"}</div>
                          <div className="col-span-2 text-xs">{b.vehicleLabel || "—"}</div>
                          <div className="col-span-2 text-xs">{b.serviceLabel || "—"}</div>
                          <div className="col-span-1 font-mono text-xs">{b.timeSlot || "—"}</div>
                          <div className="col-span-2">
                            <span className={`text-[10px] font-mono-accent px-2 py-1 ${getBookingStatusInfo(b.status).color}`}>
                              {getBookingStatusInfo(b.status).label}
                            </span>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
              {!loading && users.length > 0 && (
                <div className="flex flex-col sm:flex-row gap-2 mb-4">
                  <Input
                    data-testid="admin-customer-search"
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    placeholder="Search by name, phone, or vehicle number…"
                    className="rounded-none border-[#D4D4D8] h-10 flex-1"
                  />
                  <Button
                    type="button"
                    data-testid="admin-export-customers"
                    onClick={exportCustomersToExcel}
                    variant="outline"
                    className="rounded-none border-[#D4D4D8] h-10 font-mono-accent text-xs whitespace-nowrap"
                  >
                    <Download size={14} className="mr-2" /> EXPORT TO EXCEL
                  </Button>
                </div>
              )}
              <div className="border border-[#D4D4D8]">
              {loading && <PageLoader label="Loading customers…" />}
              {!loading && users.length === 0 && (
                <div className="p-12 text-center">
                  <Users size={32} className="mx-auto text-[#525252] mb-4" strokeWidth={1.5} />
                  <div className="font-display font-bold text-xl mb-2">No customers yet</div>
                  <p className="text-sm text-[#525252] mb-6">
                    Customers appear here automatically when they sign up with OTP, or you can add them manually.
                  </p>
                  <AdminAddUserDialog onCreated={load} />
                </div>
              )}
              {!loading && users.length > 0 && filteredCustomers.length === 0 && (
                <div className="p-6 text-center bg-[#F4F4F5]">
                  <div className="font-mono-accent text-xs text-[#525252]">
                    No customer matches "{customerSearch}"
                  </div>
                </div>
              )}
              {filteredCustomers.length > 0 && (
                <>
                  <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-[#F4F4F5] font-mono-accent text-[10px] text-[#525252]">
                <div className="col-span-3">NAME</div>
                <div className="col-span-2">PHONE</div>
                <div className="col-span-2">VEHICLE</div>
                <div className="col-span-2">SUBSCRIPTION</div>
                <div className="col-span-2">JOINED</div>
                <div className="col-span-1">ROLE</div>
              </div>
              {filteredCustomers.map((u) => {
                const userSub = subs.find((s) => s.uid === u.id && (s.status === "active" || s.status === "pending_approval"));
                const userCars = carsByUid[u.id] || [];
                const joinedDate = u.createdAt?.toDate
                  ? u.createdAt.toDate().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                  : "—";
                return (
                <div key={u.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-t border-[#D4D4D8] items-center text-sm">
                  <div className="col-span-3 font-bold">
                    <button
                      type="button"
                      data-testid={`customer-history-${u.id}`}
                      onClick={() => setHistoryFor({ id: u.id, name: u.name, phone: u.phone })}
                      className="hover:text-[#002FA7] hover:underline text-left"
                    >
                      {u.name || "—"}
                    </button>
                  </div>
                  <div className="col-span-2 font-mono text-xs">{u.phone || u.email || "—"}</div>
                  <div className="col-span-2 font-mono text-xs">
                    {userCars.length > 0
                      ? userCars.map((c) => c.registration || "no reg").join(", ")
                      : "—"}
                  </div>
                  <div className="col-span-2">
                    {userSub ? (
                      <div>
                        <div className="font-bold text-xs">{userSub.planName}</div>
                        <div className={`font-mono-accent text-[10px] mt-0.5 ${
                          userSub.status === "active" ? "text-[#00D05A]"
                          : userSub.status === "pending_approval" ? "text-[#B58900]"
                          : "text-[#525252]"
                        }`}>
                          ● {userSub.status === "pending_approval" ? "PENDING" : userSub.status?.toUpperCase()}
                          {userSub.status === "active" && userSub.washesRemaining !== undefined && ` · ${userSub.washesRemaining} LEFT`}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-[#525252]">No active plan</span>
                    )}
                  </div>
                  <div className="col-span-2 font-mono text-xs text-[#525252]">{joinedDate}</div>
                  <div className="col-span-1">
                    <span className={`text-[10px] font-mono-accent px-2 py-1 ${u.role === "admin" ? "bg-[#002FA7] text-white" : "bg-[#F4F4F5] text-[#525252]"}`}>
                      {u.role?.toUpperCase()}
                    </span>
                  </div>
                </div>
                );
              })}
                </>
              )}
              </div>
            </div>
          )}

          {tab === "products" && (
            <div>
              <div className="flex justify-between items-baseline mb-6 flex-wrap gap-2">
                <h2 className="font-display font-bold text-2xl">Product Inventory</h2>
                <div className="text-xs text-[#525252]">Add products here, then select them while billing a customer.</div>
              </div>

              {/* ADD PRODUCT */}
              <div className="border border-[#D4D4D8] bg-[#F4F4F5] p-6 mb-8">
                <div className="font-mono-accent text-xs text-[#525252] mb-3">ADD PRODUCT</div>
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="flex-1 min-w-[200px]">
                    <label className="font-mono-accent text-[10px] text-[#525252] block mb-1">NAME</label>
                    <Input
                      data-testid="new-product-name"
                      value={newProduct.name}
                      onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                      placeholder="e.g. Car Freshener"
                      className="rounded-none border-[#D4D4D8] h-10 bg-white"
                    />
                  </div>
                  <div className="w-32">
                    <label className="font-mono-accent text-[10px] text-[#525252] block mb-1">PRICE (₹)</label>
                    <Input
                      data-testid="new-product-price"
                      type="number"
                      value={newProduct.price}
                      onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                      placeholder="0"
                      className="rounded-none border-[#D4D4D8] h-10 bg-white"
                    />
                  </div>
                  <div className="w-32">
                    <label className="font-mono-accent text-[10px] text-[#525252] block mb-1">QUANTITY</label>
                    <Input
                      data-testid="new-product-quantity"
                      type="number"
                      value={newProduct.quantity}
                      onChange={(e) => setNewProduct({ ...newProduct, quantity: e.target.value })}
                      placeholder="0"
                      className="rounded-none border-[#D4D4D8] h-10 bg-white"
                    />
                  </div>
                  <Button
                    data-testid="add-product-button"
                    onClick={addProduct}
                    disabled={savingProduct}
                    className="bg-[#002FA7] hover:bg-[#001B6B] rounded-none font-bold uppercase tracking-wide text-xs h-10 px-5"
                  >
                    {savingProduct ? <Spinner /> : <><Plus size={14} className="mr-1" /> ADD</>}
                  </Button>
                </div>
              </div>

              {/* PRODUCT LIST */}
              <div className="border border-[#D4D4D8]">
                {loading && <PageLoader label="Loading products…" />}
                {!loading && products.length === 0 && (
                  <div className="p-12 text-center">
                    <Package size={32} className="mx-auto text-[#525252] mb-4" strokeWidth={1.5} />
                    <div className="font-display font-bold text-xl mb-2">No products yet</div>
                    <p className="text-sm text-[#525252]">Add your first product above — wax, air fresheners, accessories, anything you sell at the counter.</p>
                  </div>
                )}
                {products.length > 0 && (
                  <>
                    <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-[#F4F4F5] font-mono-accent text-[10px] text-[#525252]">
                      <div className="col-span-5">NAME</div>
                      <div className="col-span-2">PRICE</div>
                      <div className="col-span-2">IN STOCK</div>
                      <div className="col-span-3">ACTIONS</div>
                    </div>
                    {products.map((p) => {
                      const editing = editingProductId === p.id;
                      const lowStock = Number(p.quantity) <= 3;
                      return (
                        <div key={p.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-t border-[#D4D4D8] items-center text-sm">
                          <div className="col-span-5 font-bold">{p.name}</div>
                          {editing ? (
                            <>
                              <div className="col-span-2">
                                <Input
                                  data-testid={`product-price-input-${p.id}`}
                                  type="number"
                                  value={productDraft.price}
                                  onChange={(e) => setProductDraft({ ...productDraft, price: e.target.value })}
                                  className="rounded-none border-[#D4D4D8] h-9 text-sm"
                                />
                              </div>
                              <div className="col-span-2">
                                <Input
                                  data-testid={`product-quantity-input-${p.id}`}
                                  type="number"
                                  value={productDraft.quantity}
                                  onChange={(e) => setProductDraft({ ...productDraft, quantity: e.target.value })}
                                  className="rounded-none border-[#D4D4D8] h-9 text-sm"
                                />
                              </div>
                              <div className="col-span-3 flex items-center gap-2">
                                <Button
                                  data-testid={`product-save-${p.id}`}
                                  onClick={() => saveProductEdit(p)}
                                  size="sm"
                                  className="bg-[#002FA7] hover:bg-[#001B6B] rounded-none text-xs h-8 px-3"
                                >
                                  SAVE
                                </Button>
                                <Button
                                  data-testid={`product-cancel-${p.id}`}
                                  onClick={cancelEditProduct}
                                  size="sm"
                                  variant="outline"
                                  className="rounded-none text-xs h-8 px-3 border-[#D4D4D8]"
                                >
                                  CANCEL
                                </Button>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="col-span-2 font-bold">₹{p.price}</div>
                              <div className="col-span-2">
                                <span className={`font-mono text-sm ${lowStock ? "text-[#FF2A2A] font-bold" : ""}`}>
                                  {p.quantity}
                                </span>
                                {lowStock && <span className="ml-2 text-[9px] font-mono-accent text-[#FF2A2A]">LOW</span>}
                              </div>
                              <div className="col-span-3 flex items-center gap-3">
                                <button
                                  type="button"
                                  data-testid={`product-edit-${p.id}`}
                                  onClick={() => startEditProduct(p)}
                                  className="text-[#525252] hover:text-[#002FA7]"
                                  title="Edit price / quantity"
                                >
                                  <Pencil size={14} />
                                </button>
                                <button
                                  type="button"
                                  data-testid={`product-delete-${p.id}`}
                                  onClick={() => removeProduct(p)}
                                  className="text-[#525252] hover:text-[#FF2A2A]"
                                  title="Remove product"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          )}

          {tab === "settings" && (
            <div className="max-w-xl">
              <h2 className="font-display font-bold text-2xl mb-6">Site Settings</h2>
              <div className="border border-[#D4D4D8] divide-y divide-[#D4D4D8]">
                <div className="flex items-center justify-between gap-4 p-6">
                  <div>
                    <div className="font-bold mb-1">Booking</div>
                    <p className="text-sm text-[#525252]">
                      When off, "Book a Wash" shows Coming Soon everywhere on the site. When on, customers can book up to {MAX_BOOKING_DAYS} days ahead.
                    </p>
                  </div>
                  <Switch
                    data-testid="toggle-booking-enabled"
                    checked={settings.bookingEnabled}
                    onCheckedChange={(v) => updateSetting("bookingEnabled", v)}
                  />
                </div>
                <div className="flex items-center justify-between gap-4 p-6">
                  <div>
                    <div className="font-bold mb-1">Subscriptions</div>
                    <p className="text-sm text-[#525252]">
                      When off, new subscription requests show Coming Soon. Existing subscribers are unaffected.
                    </p>
                  </div>
                  <Switch
                    data-testid="toggle-subscription-enabled"
                    checked={settings.subscriptionEnabled}
                    onCheckedChange={(v) => updateSetting("subscriptionEnabled", v)}
                  />
                </div>
              </div>

              <h2 className="font-display font-bold text-2xl mb-6 mt-12">Data Cleanup</h2>
              <div className="border border-[#D4D4D8] p-6">
                <div className="font-bold mb-1">Merge Duplicate Customers</div>
                <p className="text-sm text-[#525252] mb-4">
                  Scans for customer records sharing the same phone number (usually caused by a manually-added
                  customer who later logged in for real), keeps the real account, moves its bookings/subscriptions/
                  vehicles over, and marks the duplicate as merged.
                </p>
                <Button
                  data-testid="merge-duplicates-button"
                  onClick={() => setConfirmMerge(true)}
                  disabled={mergingDup}
                  variant="outline"
                  className="rounded-none border-2 border-black hover:bg-black hover:text-white font-bold uppercase tracking-wide text-xs h-10 px-5"
                >
                  {mergingDup ? <Spinner /> : "Scan & Merge Duplicates"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      <AlertDialog open={!!pendingStatus} onOpenChange={(o) => { if (!o) setPendingStatus(null); }}>
        <AlertDialogContent className="rounded-none border-[#D4D4D8]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-black text-2xl tracking-tighter">Change booking status?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#525252]">
              Move from <span className="font-bold capitalize">{pendingStatus?.currentStatus}</span> → <span className="font-bold capitalize">{pendingStatus?.newStatus}</span>. This cannot be undone automatically.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none font-bold uppercase tracking-wide text-xs">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-none bg-[#002FA7] hover:bg-[#001B6B] font-bold uppercase tracking-wide text-xs"
              onClick={() => {
                if (pendingStatus) updateBookingStatus(pendingStatus.id, pendingStatus.newStatus);
                setPendingStatus(null);
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmMerge} onOpenChange={setConfirmMerge}>
        <AlertDialogContent className="rounded-none border-[#D4D4D8]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-black text-2xl tracking-tighter">Merge duplicate customers?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#525252]">
              This scans every customer for shared phone numbers, keeps the real logged-in account for each,
              and moves any bookings, subscriptions, and vehicles from the duplicate onto it. The duplicate
              record is kept but marked as merged, not deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none font-bold uppercase tracking-wide text-xs">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-none bg-[#002FA7] hover:bg-[#001B6B] font-bold uppercase tracking-wide text-xs"
              onClick={() => { setConfirmMerge(false); mergeDuplicateCustomers(); }}
            >
              Merge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!historyFor} onOpenChange={(o) => { if (!o) setHistoryFor(null); }}>
        <DialogContent className="max-w-3xl rounded-none border-[#D4D4D8] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl tracking-tighter">
              {historyFor?.name || "Customer"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-px bg-[#D4D4D8] border border-[#D4D4D8]">
              <div className="bg-white p-4">
                <div className="font-mono-accent text-[10px] text-[#525252] mb-1">PHONE</div>
                <div className="font-bold text-sm">{historyFor?.phone || "—"}</div>
              </div>
              <div className="bg-white p-4">
                <div className="font-mono-accent text-[10px] text-[#525252] mb-1">TOTAL VISITS</div>
                <div className="font-display font-black text-xl">{historyStats.totalVisits}</div>
              </div>
              <div className="bg-white p-4">
                <div className="font-mono-accent text-[10px] text-[#525252] mb-1">TOTAL SPENT</div>
                <div className="font-display font-black text-xl">₹{historyStats.totalSpent.toLocaleString("en-IN")}</div>
              </div>
            </div>

            <div>
              <div className="font-mono-accent text-xs text-[#525252] mb-2">SERVICE HISTORY</div>
              {historyBookings.length === 0 ? (
                <div className="p-8 text-center text-sm text-[#525252] border border-[#D4D4D8]">
                  No bookings yet.
                </div>
              ) : (
                <div className="border border-[#D4D4D8]">
                  {historyBookings.map((b) => (
                    <div key={b.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-[#D4D4D8] last:border-b-0 text-sm">
                      <div>
                        <div className="font-mono text-xs text-[#525252]">{b.date} · {b.timeSlot}</div>
                        <div className="font-bold">{b.serviceLabel} <span className="font-normal text-[#525252]">({b.vehicleLabel})</span></div>
                        {b.customServices?.length > 0 && (
                          <div className="text-[11px] text-[#525252] mt-0.5">
                            {b.customServices.map((s, i) => (
                              <span key={i}>+ {s.name} (₹{s.fee}){i < b.customServices.length - 1 ? ", " : ""}</span>
                            ))}
                          </div>
                        )}
                        {b.products?.length > 0 && (
                          <div className="text-[11px] text-[#525252] mt-0.5">
                            {b.products.map((p, i) => (
                              <span key={i}>· {p.qty}× {p.name}{i < b.products.length - 1 ? ", " : ""}</span>
                            ))}
                          </div>
                        )}
                        {b.priceNote && (
                          <div className="text-[11px] text-[#525252] italic mt-0.5">Note: {b.priceNote}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-[10px] font-mono-accent px-2 py-1 ${getBookingStatusInfo(b.status).color}`}>
                          {getBookingStatusInfo(b.status).label}
                        </span>
                        <div className="font-display font-bold text-lg">₹{b.price}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
